import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Input from '@mui/material/Input'
import TextField from '@mui/material/TextField'
import { PPDialog } from 'components/PPDialog'
import useAsyncAction from 'hooks/useAsyncAction'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { connect, useDispatch } from 'react-redux'
import { NavLink, useNavigate } from 'react-router'
import { Observable } from 'rxjs'
import { authErrorSelector, login, logout, PASSWORD_MIN_LENGTH, requestPassword } from 'services/auth'
import { message } from 'services/snackbar'
import StoreState, { StoreThunkDispatch } from 'store/state'
import { isEmailValid } from 'utils/email'
import { fromNow } from 'utils/format/date'
import styles from './EmailLoginForm.pcss'

const LOADER_SIZE = 20

interface EmailLoginFormOwnProps {
  fbLoginSection: React.ReactNode
  loginParams?: Partial<Record<string, string>>
  onLoginError: (error: Error) => void
}

interface ConnectedEmailLoginFormProps {
  authError?: {
    retryAt?: number
    restrictedAccess?: boolean
  }
  login: (email: string, password: string) => Observable<any>
  logout: (callback: () => void) => void
  requestNewPassword: (email: string) => Observable<any>
}

export type EmailLoginFormProps = EmailLoginFormOwnProps & ConnectedEmailLoginFormProps

export function EmailLoginForm(props: EmailLoginFormProps) {
  const dispatch = useDispatch()
  const { loginParams, onLoginError } = props
  const intl = useIntl()
  const navigate = useNavigate()
  const { logout } = props
  const [login, loginResponse, loginError, loginLoading] = useAsyncAction(props.login)
  const [logoutLoading, setLogoutLoading] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [error, setError] = React.useState<null | string>(null)
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = React.useState(false)
  const [passwordResetEmail, setPasswordResetEmail] = React.useState('')
  const [passwordResetError, setPasswordResetError] = React.useState(false)

  const { ott, hash, redirectUrl } = props.loginParams || {}

  React.useEffect(() => {
    // If One-time-token is provided, automatically log the user in with it.
    if (ott) {
      setLogoutLoading(true)
      logout(() => {
        login('', '', { ott, hash })
        setLogoutLoading(false)
      })
    } else if (hash) {
      // If hash param is provided, logout the user and let them manually log in
      logout(() => {
        setLogoutLoading(false)
      })
    }
  }, [ott, hash, login, logout])

  React.useEffect(() => {
    if (loginError) {
      const err = loginError as any
      if (err.code === 'email-not-verified') {
        navigate('/signup/verify', { state: { ott: err.data?.loginOtt } })
        return
      }
      setError(loginError.message)
      onLoginError(loginError)
    } else if (loginResponse && ott) {
      const decodedRedirectUrl = redirectUrl ? decodeURIComponent(redirectUrl) : '/content'
      navigate(decodedRedirectUrl)
    }
  }, [loginError, loginResponse, redirectUrl, onLoginError, navigate, ott, intl, dispatch])

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null)
    const value = e.target.value
    setEmail(value)
  }

  const onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null)
    const value = e.target.value
    setPassword(value)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  const handleSubmit = () => {
    setError(null)
    const mail = email.trim()
    if (!isEmailValid(mail)) {
      setError(intl.formatMessage({ id: 'login.error.wrong-mail-or-password' }))
      return
    }

    if (password.length < PASSWORD_MIN_LENGTH) {
      setError(intl.formatMessage({ id: 'login.error.wrong-mail-or-password' }))
      return
    }

    login(email, password, { ott, hash })
  }

  const closePasswordResetDialog = () => {
    setPasswordResetDialogOpen(false)
    setPasswordResetEmail('')
    setPasswordResetError(false)
  }

  const openPasswordResetDialog = () => {
    setPasswordResetDialogOpen(true)
  }

  const onPasswordResetEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordResetEmail(event.target.value)
  }

  const requestNewPassword = () => {
    setPasswordResetError(false)
    const email = passwordResetEmail.trim()
    if (!isEmailValid(email)) {
      setPasswordResetError(true)
      return
    }

    closePasswordResetDialog()
    props.requestNewPassword(email).subscribe(() => {
      dispatch(message(intl.formatMessage({ id: 'login.pw-reset.email-sent' }), 'success'))
    }, (e: Error) => {
      console.log('Forgotten password reset error: ', e)
      dispatch(message(intl.formatMessage({ id: 'errors.generic-support' }), 'error'))
    })
  }

  const loginBlocked = Boolean(props.authError?.retryAt) && new Date(props.authError?.retryAt as number).getTime() > Date.now()
  const retryAtTime = loginBlocked && fromNow(props.authError?.retryAt as any)

  return (
    <section className={styles.wrapper}>
      <form className={styles.form}>
        <div className={styles['row-input']}>
          <TextField
            type="email"
            autoComplete="username"
            className={styles.input}
            placeholder={intl.formatMessage({ id: 'login.input.email-hint' })}
            onChange={onEmailChange}
            onKeyDown={onKeyDown}
          />
        </div>

        <div className={styles['row-input']}>
          <TextField
            type="password"
            autoComplete="password"
            className={styles.input}
            placeholder={intl.formatMessage({ id: 'login.input.password-hint' })}
            onChange={onPasswordChange}
            onKeyDown={onKeyDown}
          />
          <a className={styles['label-pw-reset']} onClick={openPasswordResetDialog}>
            <FormattedMessage id="login.actions.reset-password" />
          </a>
        </div>

        <Button
          className={`${loginLoading || logoutLoading ? styles['btn-loading'] : ''} ${styles['btn-login']}`}
          variant="contained"
          color="primary"
          disabled={loginLoading || loginBlocked || logoutLoading}
          onClick={handleSubmit}
        >
          {loginLoading || logoutLoading ? (
            <CircularProgress className={styles.loader} size={LOADER_SIZE} />
          ) : (
            <FormattedMessage id="login.btn-email-login" />
          )}
        </Button>
        {props.fbLoginSection}

        {error && <p className={styles.error}>{error}</p>}
        {loginBlocked && (
          <p className={styles.error}>
            <FormattedMessage id="login.error.max-attempts-reached" values={{ time: retryAtTime }} />
          </p>
        )}
        {props.authError?.restrictedAccess && (
          <p className={styles.error}>
            <FormattedMessage id="login.error.restricted-access" />
          </p>
        )}
        <p className={styles.signup}>
          <FormattedMessage id="login.no-account" />
          <NavLink to="/signup/create-account">
            <FormattedMessage id="login.signup" />
          </NavLink>
        </p>
      </form>
      <PPDialog
        open={passwordResetDialogOpen}
        onClose={closePasswordResetDialog}
        size="sm"
        title={<FormattedMessage id="login.pw-reset.title" />}
      >
        <div className={styles['pw-reset-content']}>
          <Input
            placeholder={intl.formatMessage({ id: 'login.pw-reset.email-placeholder' })}
            type="email"
            className={styles['pw-reset-input']}
            value={passwordResetEmail}
            onChange={onPasswordResetEmailChange}
          />
          <p className={styles['pw-reset-error']}>{passwordResetError && <FormattedMessage id="login.pw-reset.label-invalid-email" />}</p>
          <Button
            variant="contained"
            className={styles['btn-pw-reset']}
            color="primary"
            disabled={loginBlocked}
            onClick={requestNewPassword}
          >
            <FormattedMessage id="login.pw-reset.actions.request-password" />
          </Button>
        </div>
      </PPDialog>
    </section>
  )
}

function mapStateToProps(state: StoreState) {
  return {
    authError: authErrorSelector(state)
  }
}

function mapDispatchToProps(dispatch: StoreThunkDispatch) {
  return {
    login: (email: string, password: string, params?: Partial<Record<string, string>>) => dispatch(login(email, password, params)),
    requestNewPassword: (email: string) => dispatch(requestPassword(email)),
    logout: (callback: () => void) => dispatch(logout(callback))
  }
}

const ConnectedEmailLoginForm = connect(mapStateToProps, mapDispatchToProps)(EmailLoginForm)
export default ConnectedEmailLoginForm
